import { type VideoHTMLAttributes, useEffect, useRef } from 'react';

interface HlsVideoProps extends VideoHTMLAttributes<HTMLVideoElement> {
  /** Video source - can be .m3u8 (HLS) or .mp4 (fallback) */
  src: string;
  /** Fallback MP4 source for browsers that don't support HLS */
  fallbackSrc?: string;
  /** Poster image to show before video loads */
  poster?: string;
}

/**
 * HLS Video component that supports adaptive bitrate streaming
 *
 * Features:
 * - Automatic HLS.js lazy loading for non-Safari browsers
 * - Native HLS support detection for Safari
 * - Fallback to MP4 if HLS is not supported
 *
 * @example
 * // HLS source
 * <HlsVideo
 *   src="/assets/video/hls/hero/master.m3u8"
 *   autoPlay
 *   muted
 *   loop
 *   playsInline
 * />
 *
 * @example
 * // With MP4 fallback
 * <HlsVideo
 *   src="/assets/video/hls/hero/master.m3u8"
 *   fallbackSrc="/assets/video/hero.mp4"
 *   autoPlay
 *   muted
 *   loop
 * />
 */
export const HlsVideo = ({ src, fallbackSrc, poster, loop, autoPlay, muted = true, ...props }: HlsVideoProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<unknown>(null);
  const isHls = src.endsWith('.m3u8');

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // CRITICAL: Set muted property programmatically for all browsers
    // This ensures autoplay works on mobile devices (autoplay policy requires muted)
    video.muted = muted;

    // For non-HLS sources, just set the src directly
    if (!isHls) {
      video.src = src;
      return;
    }

    // Safari supports HLS natively
    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = src;

      // Handle looping for Safari native HLS
      if (loop) {
        const handleEnded = () => {
          video.currentTime = 0;
          video.play().catch((e) => console.error('Failed to restart video loop:', e));
        };
        video.addEventListener('ended', handleEnded);

        return () => {
          video.removeEventListener('ended', handleEnded);
        };
      }
      return;
    }

    // Dynamically import HLS.js only when needed
    let cleanup: (() => void) | undefined;

    const initHls = async () => {
      try {
        const Hls = (await import('hls.js')).default;

        // Use HLS.js for other browsers
        if (Hls.isSupported()) {
          const hls = new Hls({
            // Instant playback - prioritize fast start over buffering
            autoStartLoad: true,
            startLevel: 0, // Start with lowest quality for instant playback
            capLevelToPlayerSize: false, // Allow quality upgrade after start

            // Minimal initial buffer for instant start
            maxBufferLength: 5, // Only 5s initial buffer (was 30s)
            maxMaxBufferLength: 30, // Max buffer ceiling (was 60s)
            maxBufferSize: 30 * 1000 * 1000, // 30MB (was 60MB)
            maxBufferHole: 0.3, // Tolerate small gaps

            // Aggressive settings for instant start
            progressive: true,
            lowLatencyMode: true, // Enable low latency mode
            backBufferLength: 0, // Don't keep old segments initially

            // Fast loading - fewer retries for speed
            manifestLoadingTimeOut: 5000,
            manifestLoadingMaxRetry: 2,
            manifestLoadingRetryDelay: 500,
            levelLoadingTimeOut: 5000,
            levelLoadingMaxRetry: 2,
            levelLoadingRetryDelay: 500,
            fragLoadingTimeOut: 10000,
            fragLoadingMaxRetry: 3,
            fragLoadingRetryDelay: 500,

            // Fast ABR decisions for quick quality upgrade
            abrEwmaDefaultEstimate: 500000,
            abrBandWidthFactor: 0.95,
            abrBandWidthUpFactor: 0.7,
            abrEwmaSlowVoD: 4, // Fast adaptation
            abrEwmaFastVoD: 2, // Very fast adaptation

            // Custom fetch setup for high priority loading
            fetchSetup: (context, initParams) => {
              return new Request(context.url, {
                ...initParams,
                priority: 'high',
              });
            },
          });

          hlsRef.current = hls;
          hls.loadSource(src);
          hls.attachMedia(video);

          // Aggressively start playback as soon as possible
          hls.on(Hls.Events.MANIFEST_PARSED, () => {
            // Start playback immediately, don't wait for full buffer
            video.play().catch(() => {
              // Autoplay might be blocked, that's OK
            });
          });

          // Also try to play on first fragment loaded for even faster start
          hls.on(Hls.Events.FRAG_LOADED, (_event: unknown, data: { frag?: { sn?: number | 'initSegment' } }) => {
            // On first fragment, start playing immediately
            if (data.frag?.sn === 0 && video.paused) {
              video.play().catch(() => {
                // Autoplay might be blocked, that's OK
              });
            }
          });

          // Handle looping for HLS streams
          if (loop) {
            const handleEnded = () => {
              // For HLS, reload the stream for smoother loop
              video.currentTime = 0;
              hls.startLoad(0);
              video.play().catch((e) => console.error('Failed to restart video loop:', e));
            };
            
            video.addEventListener('ended', handleEnded);
            
            // Clean up event listener
            const originalCleanup = cleanup;
            cleanup = () => {
              video.removeEventListener('ended', handleEnded);
              originalCleanup?.();
            };
          }

          // Improved error handling
          let networkErrorCount = 0;
          let mediaErrorCount = 0;

          hls.on(Hls.Events.ERROR, (_event: unknown, data: { fatal?: boolean; type?: string; details?: string }) => {
            console.warn('HLS Error:', data);

            if (data.fatal) {
              switch (data.type) {
                case Hls.ErrorTypes.NETWORK_ERROR:
                  networkErrorCount++;
                  console.error('Fatal network error, attempt recovery...', networkErrorCount);

                  if (networkErrorCount < 3) {
                    // Try to recover from network errors
                    setTimeout(() => {
                      hls.startLoad();
                    }, 1000);
                  } else if (fallbackSrc) {
                    // After 3 attempts, fall back to MP4
                    console.error('Too many network errors, switching to fallback');
                    video.src = fallbackSrc;
                    hls.destroy();
                  }
                  break;

                case Hls.ErrorTypes.MEDIA_ERROR:
                  mediaErrorCount++;
                  console.error('Fatal media error, attempt recovery...', mediaErrorCount);

                  if (mediaErrorCount < 2) {
                    // Try to recover from media errors
                    setTimeout(() => {
                      hls.recoverMediaError();
                    }, 100);
                  } else if (fallbackSrc) {
                    // After 2 attempts, fall back to MP4
                    console.error('Too many media errors, switching to fallback');
                    video.src = fallbackSrc;
                    hls.destroy();
                  }
                  break;

                default:
                  console.error('Fatal HLS error, cannot recover');
                  // Fall back to MP4 if available
                  if (fallbackSrc) {
                    video.src = fallbackSrc;
                  }
                  hls.destroy();
                  break;
              }
            }
          });

          cleanup = () => {
            hls.destroy();
            hlsRef.current = null;
          };
        } else if (fallbackSrc) {
          // HLS.js not supported, use fallback
          video.src = fallbackSrc;
        }
      } catch (error) {
        console.error('Failed to load HLS.js:', error);
        if (fallbackSrc) {
          video.src = fallbackSrc;
        }
      }
    };

    initHls();

    return () => {
      cleanup?.();
    };
  }, [src, fallbackSrc, isHls, loop, muted]);



  return (
    // eslint-disable-next-line jsx-a11y/media-has-caption
    <video
      ref={videoRef}
      muted={muted}
      poster={poster}
      loop={loop}
      preload="auto"
      autoPlay={autoPlay}
      playsInline
      // @ts-expect-error - fetchPriority is valid but not in React types yet
      fetchPriority="high"
      {...props}
    />
  );
};
